import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Check, Circle, ExternalLink, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
} from '@zt/ui';
import type { ai } from '@zt/core';
import { ConfirmButton } from '../../components/Confirm';
import { ImageUpload } from '../../components/ImageUpload';
import { ProductMultiPicker } from '../../components/ProductMultiPicker';
import { api, newIdempotencyKey, type Jsonify } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { fieldError, useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';
import type { PostCategoryList } from '../../lib/types';
import { StorePicker, useStoreChoice } from '../payments';
import { LocalePicker, useContentLocale } from '../../components/ContentLocale';
import { AiButton } from './shared';

/**
 * İçerik Stüdyosu (F9-05, ADR-129): 27 adımlı makale süreci. Yapay zekâ adımları (plan, taslak, SEO önerisi)
 * isteğe bağlıdır; her alan elle de doldurulur. Sonuç blog yazısına taslak olarak aktarılır; editör ve veteriner
 * onayı ile zamanlı yayın blog ekranındadır.
 */

type R<F extends (...a: never[]) => unknown> = Jsonify<Awaited<ReturnType<F>>>;
type ProjectList = R<typeof ai.studio.listProjects>;
type Detail = R<typeof ai.studio.getProject>;
type Project = Detail['project'];
type Suggestions = R<typeof ai.studio.linkSuggestions>;
type AiResult = R<typeof ai.studio.aiPlan>;

const INTENTS = ['informational', 'commercial', 'transactional', 'navigational'] as const;
const STATUS_TONE: Record<string, Tone> = { draft: 'neutral', exported: 'moss', archived: 'slate' };
const QUALITY_TONE: Record<string, Tone> = { pass: 'moss', warn: 'amber', fail: 'rose' };
const POST_TONE: Record<string, Tone> = {
  draft: 'neutral',
  scheduled: 'amber',
  live: 'moss',
  unpublished: 'slate',
};

/** Adım → ekrandaki bölüm (adım listesindeki bağlantılar için). */
const SECTION_OF: Record<string, string> = {
  topic: 'brief',
  audience: 'brief',
  market: 'brief',
  keyword: 'brief',
  intent: 'brief',
  sources: 'research',
  duplicates: 'research',
  plan: 'outline',
  headings: 'outline',
  draft: 'draft',
  sourceCheck: 'research',
  internalLinks: 'draft',
  relatedProducts: 'draft',
  seoTitle: 'seo',
  metaDescription: 'seo',
  slug: 'seo',
  category: 'seo',
  tags: 'seo',
  schema: 'seo',
  cover: 'images',
  images: 'images',
  altText: 'images',
  quality: 'quality',
  approval: 'publish',
  schedule: 'publish',
  performance: 'performance',
  updates: 'performance',
};

// ---------------------------------------------------------------------------------------------------- Liste

export function ContentStudioPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const [status, setStatus] = useState<'' | 'exported' | 'archived'>('');
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ['studio', 'list', storeId, status],
    queryFn: () =>
      api.get<ProjectList>(
        `/v1/admin/content-studio/projects?storeId=${storeId}${status ? `&status=${status}` : ''}`,
      ),
    enabled: !!storeId,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame
      helpKey="page.content_studio"
      actions={
        can('content.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('ai.studio.create')}
            </Button>
            <HelpTip k="studio.create" />
          </div>
        )
      }
    >
      <p className="max-w-[70ch] text-sm text-ink-2">{t('ai.studio.lead')}</p>
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <Field label={t('ai.studio.filter')} className="w-56">
              {(p) => (
                <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                  <option value="">{t('ai.studio.filters.active')}</option>
                  <option value="exported">{t('ai.studio.filters.exported')}</option>
                  <option value="archived">{t('ai.studio.filters.archived')}</option>
                </Select>
              )}
            </Field>
          </div>
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !list.data.length ? (
            <EmptyState title={t('ai.studio.empty')} description={t('ai.studio.emptyLead')} />
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {list.data.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/yapay-zeka/icerik-studyosu/$id"
                      params={{ id: p.id }}
                      className="font-medium text-pine hover:underline"
                    >
                      {p.title}
                    </Link>
                    <p className="text-ink-3">
                      {p.keyword ? `${p.keyword} · ` : ''}
                      {t('ai.studio.words', { count: p.words })} ·{' '}
                      {t('ai.studio.updated', {
                        date: formatDate(p.updatedAt, true),
                        name: p.createdByName ?? '—',
                      })}
                    </p>
                  </div>
                  {p.healthContent && <Badge tone="amber">{t('ai.studio.healthBadge')}</Badge>}
                  {p.aiAssisted && (
                    <Badge tone="pine">
                      <Sparkles className="me-1 inline h-3.5 w-3.5" aria-hidden />
                      {t('ai.studio.aiAssisted')}
                    </Badge>
                  )}
                  <Badge tone={STATUS_TONE[p.status]}>{t(`ai.studio.statuses.${p.status}`)}</Badge>
                </li>
              ))}
            </ul>
          )}
          {creating && <CreateDialog storeId={storeId} onClose={() => setCreating(false)} />}
        </>
      )}
    </PageFrame>
  );
}

function CreateDialog({ storeId, onClose }: { storeId: string; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const [f, setF] = useState({
    topic: '',
    audience: '',
    keywordId: '',
    keyword: '',
    intent: '',
    country: 'TR',
    healthContent: false,
  });
  const pool = useQuery({
    queryKey: ['keywords', storeId],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; keyword: string; locale: string; intent: string | null }> }>(
        `/v1/admin/keywords?storeId=${storeId}`,
      ),
    enabled: can('seo.read'),
  });
  const create = useApiMutation(
    () =>
      api.post<{ id: string }>('/v1/admin/content-studio/projects', {
        storeId,
        locale: locale ?? undefined,
        topic: f.topic,
        audience: f.audience || null,
        keywordId: f.keywordId || null,
        keyword: f.keywordId ? null : f.keyword || null,
        intent: f.intent || null,
        country: f.country,
        healthContent: f.healthContent,
      }),
    {
      invalidate: [['studio', 'list']],
      silentFieldErrors: true,
      onSuccess: (r) => {
        onClose();
        void navigate({ to: '/yapay-zeka/icerik-studyosu/$id', params: { id: r.id } });
      },
    },
  );
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const keywords = (pool.data?.items ?? []).filter((k) => !locale || k.locale === locale);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('ai.studio.createTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={f.topic.trim().length < 3}
            loading={create.isPending}
            onClick={() => create.mutate(undefined)}
          >
            {t('ai.studio.createSubmit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
        <Field
          label={t('ai.studio.f.topic')}
          description={t('ai.studio.f.topicHint')}
          help={<HelpTip k="studio.brief" />}
          error={fieldError(create.error, 'topic')}
        >
          {(p) => (
            <Input {...p} value={f.topic} maxLength={300} onChange={(e) => set('topic', e.target.value)} />
          )}
        </Field>
        <Field label={t('ai.studio.f.audience')} error={fieldError(create.error, 'audience')}>
          {(p) => (
            <Input
              {...p}
              value={f.audience}
              maxLength={300}
              onChange={(e) => set('audience', e.target.value)}
            />
          )}
        </Field>
        {keywords.length > 0 && (
          <Field label={t('ai.studio.f.keywordPool')} help={<HelpTip k="studio.keyword" />}>
            {(p) => (
              <Select
                {...p}
                value={f.keywordId}
                onChange={(e) => {
                  const k = keywords.find((x) => x.id === e.target.value);
                  setF((x) => ({ ...x, keywordId: e.target.value, intent: k?.intent ?? x.intent }));
                }}
              >
                <option value="">{t('ai.studio.f.keywordNone')}</option>
                {keywords.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.keyword}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {!f.keywordId && (
          <Field label={t('ai.studio.f.keyword')} error={fieldError(create.error, 'keyword')}>
            {(p) => (
              <Input
                {...p}
                value={f.keyword}
                maxLength={120}
                onChange={(e) => set('keyword', e.target.value)}
              />
            )}
          </Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('ai.studio.f.intent')}>
            {(p) => (
              <Select {...p} value={f.intent} onChange={(e) => set('intent', e.target.value)}>
                <option value="">{t('ai.studio.f.intentNone')}</option>
                {INTENTS.map((i) => (
                  <option key={i} value={i}>
                    {t(`ai.studio.intents.${i}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('ai.studio.f.country')}
            description={t('ai.studio.f.countryHint')}
            error={fieldError(create.error, 'country')}
          >
            {(p) => (
              <Input
                {...p}
                value={f.country}
                maxLength={2}
                onChange={(e) => set('country', e.target.value.toUpperCase())}
              />
            )}
          </Field>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={f.healthContent}
            onCheckedChange={(v) => set('healthContent', v)}
            label={t('ai.studio.f.health')}
          />
          <span className="text-sm">{t('ai.studio.f.health')}</span>
          <HelpTip k="studio.health" />
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------- Proje

interface Form {
  title: string;
  topic: string;
  audience: string;
  country: string;
  keyword: string;
  intent: string;
  healthContent: boolean;
  sources: Array<{ url: string; title: string; note: string }>;
  plan: string;
  headings: Array<{ level: 2 | 3; text: string }>;
  draft: string;
  internalLinks: Project['internalLinks'];
  relatedProductIds: string[];
  seoTitle: string;
  metaDescription: string;
  slug: string;
  categoryId: string;
  tags: string;
  coverFileId: string | null;
  coverAlt: string;
  images: Array<{ fileId: string; alt: string; aiGenerated: boolean }>;
  aiDisclosure: boolean;
}

function formOf(p: Project): Form {
  return {
    title: p.title,
    topic: p.topic,
    audience: p.audience ?? '',
    country: p.country,
    keyword: p.keyword ?? '',
    intent: p.intent ?? '',
    healthContent: p.healthContent,
    sources: p.sources.map((s) => ({ url: s.url, title: s.title ?? '', note: s.note ?? '' })),
    plan: p.plan ?? '',
    headings: p.headings,
    draft: p.draft,
    internalLinks: p.internalLinks,
    relatedProductIds: p.relatedProductIds,
    seoTitle: p.seoTitle ?? '',
    metaDescription: p.metaDescription ?? '',
    slug: p.slug ?? '',
    categoryId: p.categoryId ?? '',
    tags: p.tags.join(', '),
    coverFileId: p.coverFileId,
    coverAlt: p.coverAlt ?? '',
    images: p.images,
    aiDisclosure: p.aiDisclosure,
  };
}

function bodyOf(f: Form, version: number) {
  const n = (s: string) => (s.trim() ? s.trim() : null);
  return {
    title: f.title,
    topic: f.topic,
    audience: n(f.audience),
    country: f.country,
    keyword: n(f.keyword),
    intent: f.intent || null,
    healthContent: f.healthContent,
    sources: f.sources
      .filter((s) => s.url.trim())
      .map((s) => ({ url: s.url.trim(), title: n(s.title), note: n(s.note) })),
    plan: n(f.plan),
    headings: f.headings.filter((h) => h.text.trim()),
    draft: f.draft,
    internalLinks: f.internalLinks,
    relatedProductIds: f.relatedProductIds,
    seoTitle: n(f.seoTitle),
    metaDescription: n(f.metaDescription),
    slug: n(f.slug),
    categoryId: f.categoryId || null,
    tags: f.tags
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
    coverFileId: f.coverFileId,
    coverAlt: n(f.coverAlt),
    images: f.images,
    aiDisclosure: f.aiDisclosure,
    version,
  };
}

const wordsOf = (md: string) =>
  md
    .replace(/[#>*_`|~-]+/g, ' ')
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w)).length;

export function ContentStudioProjectPage() {
  const t = useT();
  const { id } = useParams({ from: '/app/yapay-zeka/icerik-studyosu/$id' });
  const q = useQuery({
    queryKey: ['studio', id],
    queryFn: () => api.get<Detail>(`/v1/admin/content-studio/projects/${id}`),
  });
  // Son yapay zekâ adımının uyarıları (sağlık beyanı, maskeleme): form yeniden kurulsa da kalır.
  const [aiNote, setAiNote] = useState<AiNoteState>(null);
  if (!q.data) return <Spinner label={t('common.loading')} />;
  // Kayıt sürümü değişince (kaydetme, yapay zekâ adımı) form sunucudaki hâliyle yeniden kurulur.
  return <Editor key={q.data.project.version} d={q.data} aiNote={aiNote} setAiNote={setAiNote} />;
}

type AiNoteState = { step: string; r: AiResult } | null;

function Editor({
  d,
  aiNote,
  setAiNote,
}: {
  d: Detail;
  aiNote: AiNoteState;
  setAiNote: (v: AiNoteState) => void;
}) {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const { can } = usePermissions();
  const p = d.project;
  const id = p.id;
  const canWrite = can('content.write') && p.status !== 'archived';
  const initial = formOf(p);
  const [f, setF] = useState<Form>(initial);
  const [suggest, setSuggest] = useState<Suggestions | null>(null);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const inv = [
    ['studio', id],
    ['studio', 'list'],
  ];

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const save = useApiMutation(
    () => api.patch(`/v1/admin/content-studio/projects/${id}`, bodyOf(f, p.version)),
    {
      invalidate: inv,
      success: t('ai.studio.saved'),
    },
  );
  const verify = useApiMutation(() => api.post(`/v1/admin/content-studio/projects/${id}/sources/verify`), {
    invalidate: inv,
    success: t('ai.studio.verified'),
  });
  const dup = useApiMutation(() => api.post(`/v1/admin/content-studio/projects/${id}/duplicates`), {
    invalidate: inv,
    success: t('ai.studio.duplicatesChecked'),
  });
  const links = useApiMutation(
    () => api.get<Suggestions>(`/v1/admin/content-studio/projects/${id}/link-suggestions`),
    { onSuccess: (r) => setSuggest(r) },
  );
  const step = useApiMutation(
    (v: { step: 'plan' | 'draft' | 'meta'; overwrite: boolean }) =>
      api.post<AiResult>(
        `/v1/admin/content-studio/projects/${id}/ai/${v.step}`,
        { overwrite: v.overwrite },
        { idempotencyKey: newIdempotencyKey() },
      ),
    {
      invalidate: [...inv, ['ai', 'availability']],
      success: t('ai.studio.aiDone'),
      onSuccess: (r, v) => setAiNote({ step: v.step, r }),
    },
  );
  const imgGen = useApiMutation(
    (v: { step: 'cover' | 'image'; prompt: string; style: string; size: string; overwrite: boolean }) =>
      api.post(
        `/v1/admin/content-studio/projects/${id}/ai/${v.step}`,
        { prompt: v.prompt, style: v.style, size: v.size, overwrite: v.overwrite },
        { idempotencyKey: newIdempotencyKey() },
      ),
    { invalidate: [...inv, ['ai', 'availability']], success: t('ai.studio.imageDone') },
  );
  const exp = useApiMutation(
    () =>
      api.post(`/v1/admin/content-studio/projects/${id}/export`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    { invalidate: [...inv, ['posts']], success: t('ai.studio.exported') },
  );
  const archive = useApiMutation(() => api.post(`/v1/admin/content-studio/projects/${id}/archive`), {
    invalidate: inv,
    success: t('ai.studio.archivedDone'),
  });
  const categories = useQuery({
    queryKey: ['post-categories', p.storeId, p.locale],
    queryFn: () =>
      api.get<PostCategoryList>(`/v1/admin/post-categories?storeId=${p.storeId}&locale=${p.locale}`),
  });

  const done = d.steps.filter((s) => s.done).length;
  const err = (k: string) => fieldError(save.error, k);
  const aiStep = (s: 'plan' | 'draft' | 'meta', filled: boolean, label: string) =>
    canWrite && (
      <AiStepButton
        filled={filled}
        dirty={dirty}
        loading={step.isPending && step.variables?.step === s}
        onRun={(overwrite) => step.mutate({ step: s, overwrite })}
      >
        {label}
      </AiStepButton>
    );
  const note = (s: string) => aiNote?.step === s && <AiNote r={aiNote.r} />;

  return (
    <PageFrame
      helpKey="page.content_studio_project"
      title={p.title}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/yapay-zeka/icerik-studyosu" className="text-sm text-pine underline underline-offset-2">
            {t('ai.studio.back')}
          </Link>
          <Badge tone={STATUS_TONE[p.status]}>{t(`ai.studio.statuses.${p.status}`)}</Badge>
          {canWrite && (
            <>
              <Button
                variant="primary"
                disabled={!dirty}
                loading={save.isPending}
                onClick={() => save.mutate(undefined)}
              >
                {t('ai.studio.save')}
              </Button>
              <HelpTip k="studio.save" />
            </>
          )}
        </div>
      }
    >
      {dirty && <p className="text-sm text-amber">{t('ai.studio.unsaved')}</p>}

      <Section
        title={t('ai.studio.stepsTitle', { done, total: d.steps.length })}
        action={<HelpTip k="studio.steps" />}
      >
        <ol className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {d.steps.map((s) => (
            <li key={s.key} className="flex items-center gap-2">
              {s.done ? (
                <Check className="h-4 w-4 text-moss" aria-hidden />
              ) : (
                <Circle className="h-4 w-4 text-ink-3" aria-hidden />
              )}
              <a href={`#bolum-${SECTION_OF[s.key]}`} className="hover:underline">
                {s.no}. {t(`ai.studio.steps.${s.key}`)}
              </a>
              <span className="sr-only">{s.done ? t('ai.studio.stepDone') : t('ai.studio.stepOpen')}</span>
              {s.ai && (
                <span title={t('ai.studio.stepAi')}>
                  <Sparkles className="h-3.5 w-3.5 text-pine" aria-hidden />
                  <span className="sr-only">{t('ai.studio.stepAi')}</span>
                </span>
              )}
            </li>
          ))}
        </ol>
      </Section>

      {/* 1–5 Konu ve hedef */}
      <div id="bolum-brief">
        <Section title={t('ai.studio.sec.brief')} action={<HelpTip k="studio.brief" />}>
          <fieldset disabled={!canWrite} className="grid gap-4 sm:grid-cols-2">
            <Field label={t('ai.studio.f.title')} error={err('title')}>
              {(x) => (
                <Input
                  {...x}
                  value={f.title}
                  maxLength={160}
                  onChange={(e) => set('title', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('ai.studio.f.topic')} error={err('topic')}>
              {(x) => (
                <Input
                  {...x}
                  value={f.topic}
                  maxLength={300}
                  onChange={(e) => set('topic', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('ai.studio.f.audience')} error={err('audience')}>
              {(x) => (
                <Input
                  {...x}
                  value={f.audience}
                  maxLength={300}
                  onChange={(e) => set('audience', e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('ai.studio.f.country')}
              description={t('ai.studio.f.countryHint')}
              error={err('country')}
            >
              {(x) => (
                <Input
                  {...x}
                  value={f.country}
                  maxLength={2}
                  onChange={(e) => set('country', e.target.value.toUpperCase())}
                />
              )}
            </Field>
            <Field
              label={t('ai.studio.f.keyword')}
              help={<HelpTip k="studio.keyword" />}
              error={err('keyword')}
            >
              {(x) => (
                <Input
                  {...x}
                  value={f.keyword}
                  maxLength={120}
                  onChange={(e) => set('keyword', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('ai.studio.f.intent')}>
              {(x) => (
                <Select {...x} value={f.intent} onChange={(e) => set('intent', e.target.value)}>
                  <option value="">{t('ai.studio.f.intentNone')}</option>
                  {INTENTS.map((i) => (
                    <option key={i} value={i}>
                      {t(`ai.studio.intents.${i}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="flex items-center gap-3">
              <Switch
                checked={f.healthContent}
                onCheckedChange={(v) => set('healthContent', v)}
                label={t('ai.studio.f.health')}
                disabled={!canWrite}
              />
              <span className="text-sm">{t('ai.studio.f.health')}</span>
              <HelpTip k="studio.health" />
            </div>
          </fieldset>
        </Section>
      </div>

      {/* 6–7, 11 Kaynaklar ve tekrar kontrolü */}
      <div id="bolum-research">
        <Section title={t('ai.studio.sec.research')} action={<HelpTip k="studio.sources" />}>
          <p className="mb-3 max-w-[70ch] text-sm text-ink-2">{t('ai.studio.sourcesLead')}</p>
          <ul className="space-y-3">
            {f.sources.map((s, i) => {
              const check = p.sources.find((x) => x.url === s.url)?.check;
              return (
                <li key={i} className="rounded-md border border-line p-3">
                  <fieldset
                    disabled={!canWrite}
                    className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end"
                  >
                    <Field label={t('ai.studio.f.sourceUrl')} error={err(`sources.${i}.url`)}>
                      {(x) => (
                        <Input
                          {...x}
                          type="url"
                          value={s.url}
                          onChange={(e) =>
                            set(
                              'sources',
                              f.sources.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)),
                            )
                          }
                        />
                      )}
                    </Field>
                    <Field label={t('ai.studio.f.sourceTitle')}>
                      {(x) => (
                        <Input
                          {...x}
                          value={s.title}
                          maxLength={300}
                          onChange={(e) =>
                            set(
                              'sources',
                              f.sources.map((y, j) => (j === i ? { ...y, title: e.target.value } : y)),
                            )
                          }
                        />
                      )}
                    </Field>
                    <Field label={t('ai.studio.f.sourceNote')}>
                      {(x) => (
                        <Input
                          {...x}
                          value={s.note}
                          maxLength={500}
                          onChange={(e) =>
                            set(
                              'sources',
                              f.sources.map((y, j) => (j === i ? { ...y, note: e.target.value } : y)),
                            )
                          }
                        />
                      )}
                    </Field>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('ai.studio.removeSource')}
                      onClick={() =>
                        set(
                          'sources',
                          f.sources.filter((_, j) => j !== i),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </fieldset>
                  {check && (
                    <p className="mt-2 text-sm text-ink-2">
                      <Badge tone={check.status === 'ok' ? 'moss' : 'rose'}>
                        {t(`ai.studio.sourceStatus.${check.status}`)}
                      </Badge>{' '}
                      {check.pageTitle ? `${check.pageTitle} · ` : ''}
                      {check.httpStatus ? `HTTP ${check.httpStatus} · ` : ''}
                      {t('ai.studio.checkedAt', { date: formatDate(check.checkedAt, true) })}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          {canWrite && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={f.sources.length >= 20}
                onClick={() => set('sources', [...f.sources, { url: '', title: '', note: '' }])}
              >
                <Plus className="h-4 w-4" aria-hidden />
                {t('ai.studio.addSource')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={dirty || !p.sources.length}
                loading={verify.isPending}
                onClick={() => verify.mutate(undefined)}
              >
                {t('ai.studio.verify')}
              </Button>
              <HelpTip k="studio.verify" />
            </div>
          )}

          <h3 className="mt-6 flex items-center gap-1.5 text-sm font-medium">
            {t('ai.studio.duplicatesTitle')}
            <HelpTip k="studio.duplicates" />
          </h3>
          {!p.duplicatesCheckedAt ? (
            <p className="text-sm text-ink-3">{t('ai.studio.duplicatesNotChecked')}</p>
          ) : !p.duplicates.length ? (
            <p className="text-sm text-ink-2">{t('ai.studio.duplicatesNone')}</p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {p.duplicates.map((x) => (
                <li key={`${x.type}-${x.id}`}>
                  {x.title} <span className="text-ink-3">({x.path})</span> ·{' '}
                  <Badge tone={x.similarity >= 0.6 ? 'amber' : 'neutral'}>
                    {t('ai.studio.similarity', { pct: Math.round(x.similarity * 100) })}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          {canWrite && (
            <Button
              size="sm"
              className="mt-2"
              disabled={dirty}
              loading={dup.isPending}
              onClick={() => dup.mutate(undefined)}
            >
              {t('ai.studio.checkDuplicates')}
            </Button>
          )}
        </Section>
      </div>

      {/* 8–9 Plan ve başlıklar */}
      <div id="bolum-outline">
        <Section
          title={t('ai.studio.sec.outline')}
          action={
            <span className="flex items-center gap-2">
              {aiStep('plan', !!(p.plan || p.headings.length), t('ai.studio.aiPlan'))}
              <HelpTip k="studio.ai_plan" />
            </span>
          }
        >
          {note('plan')}
          <fieldset disabled={!canWrite} className="space-y-4">
            <Field label={t('ai.studio.f.plan')} error={err('plan')}>
              {(x) => (
                <Textarea
                  {...x}
                  rows={4}
                  value={f.plan}
                  maxLength={5000}
                  onChange={(e) => set('plan', e.target.value)}
                />
              )}
            </Field>
            <div>
              <p className="mb-2 text-sm font-medium">{t('ai.studio.f.headings')}</p>
              <ul className="space-y-2">
                {f.headings.map((h, i) => (
                  <li key={i} className="flex items-end gap-2">
                    <Field label={t('ai.studio.f.headingLevel')} className="w-24">
                      {(x) => (
                        <Select
                          {...x}
                          value={h.level}
                          onChange={(e) =>
                            set(
                              'headings',
                              f.headings.map((y, j) =>
                                j === i ? { ...y, level: Number(e.target.value) as 2 | 3 } : y,
                              ),
                            )
                          }
                        >
                          <option value={2}>H2</option>
                          <option value={3}>H3</option>
                        </Select>
                      )}
                    </Field>
                    <Field
                      label={t('ai.studio.f.headingText')}
                      className={h.level === 3 ? 'flex-1 ps-6' : 'flex-1'}
                    >
                      {(x) => (
                        <Input
                          {...x}
                          value={h.text}
                          maxLength={160}
                          onChange={(e) =>
                            set(
                              'headings',
                              f.headings.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)),
                            )
                          }
                        />
                      )}
                    </Field>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('ai.studio.removeHeading')}
                      onClick={() =>
                        set(
                          'headings',
                          f.headings.filter((_, j) => j !== i),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
              {canWrite && (
                <Button
                  size="sm"
                  className="mt-2"
                  disabled={f.headings.length >= 40}
                  onClick={() => set('headings', [...f.headings, { level: 2, text: '' }])}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  {t('ai.studio.addHeading')}
                </Button>
              )}
            </div>
          </fieldset>
        </Section>
      </div>

      {/* 10, 12–13 Taslak, iç bağlantılar, ilgili ürünler */}
      <div id="bolum-draft">
        <Section
          title={t('ai.studio.sec.draft')}
          action={
            <span className="flex items-center gap-2">
              {aiStep('draft', !!p.draft.trim(), t('ai.studio.aiDraft'))}
              <HelpTip k="studio.ai_draft" />
            </span>
          }
        >
          {note('draft')}
          <Field
            label={t('ai.studio.f.draft')}
            description={t('ai.studio.f.draftHint', { count: wordsOf(f.draft) })}
            help={<HelpTip k="studio.draft" />}
            error={err('draft')}
          >
            {(x) => (
              <Textarea
                {...x}
                rows={18}
                className="font-mono text-[0.875rem]"
                value={f.draft}
                readOnly={!canWrite}
                onChange={(e) => set('draft', e.target.value)}
              />
            )}
          </Field>

          <h3 className="mt-6 flex items-center gap-1.5 text-sm font-medium">
            {t('ai.studio.linksTitle')}
            <HelpTip k="studio.links" />
          </h3>
          {f.internalLinks.length ? (
            <ul className="mt-1 space-y-1 text-sm">
              {f.internalLinks.map((l) => (
                <li key={`${l.type}-${l.refId}`} className="flex items-center gap-2">
                  <Badge>{t(`ai.studio.linkTypes.${l.type}`)}</Badge>
                  {l.title} <code className="text-ink-3">{l.path}</code>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('ai.studio.removeLink', { title: l.title })}
                      onClick={() =>
                        set(
                          'internalLinks',
                          f.internalLinks.filter((y) => y !== l),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-3">{t('ai.studio.linksNone')}</p>
          )}
          {canWrite && (
            <Button
              size="sm"
              className="mt-2"
              loading={links.isPending}
              onClick={() => links.mutate(undefined)}
            >
              {t('ai.studio.suggest')}
            </Button>
          )}
          {suggest && (
            <div className="mt-3 rounded-md border border-line p-3">
              {!suggest.internalLinks.length ? (
                <p className="text-sm text-ink-3">{t('ai.studio.suggestNone')}</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {suggest.internalLinks.map((l) => {
                    const added = f.internalLinks.some((y) => y.type === l.type && y.refId === l.refId);
                    return (
                      <li key={`${l.type}-${l.refId}`} className="flex flex-wrap items-center gap-2">
                        <Badge>{t(`ai.studio.linkTypes.${l.type}`)}</Badge>
                        {l.title} <code className="text-ink-3">{l.path}</code>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={added || f.internalLinks.length >= 30}
                          onClick={() =>
                            set('internalLinks', [
                              ...f.internalLinks,
                              { type: l.type, refId: l.refId, title: l.title, path: l.path },
                            ])
                          }
                        >
                          {added ? t('ai.studio.added') : t('ai.studio.add')}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
          <div className="mt-6">
            <ProductMultiPicker
              value={f.relatedProductIds}
              onChange={(v) => set('relatedProductIds', v)}
              label={t('ai.studio.f.relatedProducts')}
              help={<HelpTip k="studio.related" />}
              error={err('relatedProductIds')}
              max={8}
            />
            {suggest && suggest.relatedProducts.length > 0 && canWrite && (
              <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-2">
                {t('ai.studio.relatedSuggested')}
                {suggest.relatedProducts.map((r) => (
                  <Button
                    key={r.id}
                    size="sm"
                    variant="ghost"
                    disabled={f.relatedProductIds.includes(r.id) || f.relatedProductIds.length >= 8}
                    onClick={() => set('relatedProductIds', [...f.relatedProductIds, r.id])}
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                    {r.title}
                  </Button>
                ))}
              </p>
            )}
          </div>
        </Section>
      </div>

      {/* 14–19 SEO */}
      <div id="bolum-seo">
        <Section
          title={t('ai.studio.sec.seo')}
          action={
            <span className="flex items-center gap-2">
              {aiStep('meta', !!(p.seoTitle || p.metaDescription), t('ai.studio.aiMeta'))}
              <HelpTip k="studio.ai_meta" />
            </span>
          }
        >
          {note('meta')}
          <fieldset disabled={!canWrite} className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('ai.studio.f.seoTitle')}
              description={t('ai.studio.chars', { count: f.seoTitle.length, max: 70 })}
              help={<HelpTip k="studio.seo" />}
              error={err('seoTitle')}
            >
              {(x) => (
                <Input
                  {...x}
                  value={f.seoTitle}
                  maxLength={70}
                  onChange={(e) => set('seoTitle', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('ai.studio.f.slug')} description={`/blog/${f.slug || '…'}`} error={err('slug')}>
              {(x) => (
                <Input
                  {...x}
                  value={f.slug}
                  maxLength={80}
                  onChange={(e) => set('slug', e.target.value.toLowerCase())}
                />
              )}
            </Field>
            <Field
              label={t('ai.studio.f.metaDescription')}
              description={t('ai.studio.chars', { count: f.metaDescription.length, max: 170 })}
              error={err('metaDescription')}
              className="sm:col-span-2"
            >
              {(x) => (
                <Textarea
                  {...x}
                  rows={2}
                  value={f.metaDescription}
                  maxLength={170}
                  onChange={(e) => set('metaDescription', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('ai.studio.f.category')} error={err('categoryId')}>
              {(x) => (
                <Select {...x} value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                  <option value="">{t('ai.studio.f.categoryNone')}</option>
                  {(categories.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('ai.studio.f.tags')} description={t('ai.studio.f.tagsHint')} error={err('tags')}>
              {(x) => <Input {...x} value={f.tags} onChange={(e) => set('tags', e.target.value)} />}
            </Field>
          </fieldset>
          <p className="mt-3 text-sm text-ink-3">{t('ai.studio.schemaNote')}</p>
        </Section>
      </div>

      {/* 20–22 Görseller */}
      <div id="bolum-images">
        <Section
          title={t('ai.studio.sec.images')}
          action={
            <span className="flex flex-wrap items-center gap-2">
              {canWrite && (
                <ImageGenButton
                  label={t('ai.studio.aiCover')}
                  sizes={false}
                  defaultPrompt={p.title}
                  filled={!!p.coverFileId}
                  dirty={dirty}
                  loading={imgGen.isPending && imgGen.variables?.step === 'cover'}
                  onRun={(b) => imgGen.mutate({ step: 'cover', ...b })}
                />
              )}
              <HelpTip k="studio.ai_cover" />
              <HelpTip k="studio.images" />
            </span>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 flex flex-wrap items-center gap-2 text-sm font-medium">
                {t('ai.studio.f.cover')}
                {p.coverAiGenerated && f.coverFileId === p.coverFileId && <AiBadge />}
              </p>
              {canWrite ? (
                <ImageUpload
                  value={f.coverFileId}
                  onChange={(v) => set('coverFileId', v)}
                  removable
                  wide
                  label={t('ai.studio.f.cover')}
                />
              ) : (
                <p className="text-sm text-ink-3">{f.coverFileId ? t('ai.studio.coverSet') : '—'}</p>
              )}
              {f.coverFileId && (
                <div className="mt-2">
                  <ProvenanceButton fileId={f.coverFileId} />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Field label={t('ai.studio.f.coverAlt')} error={err('coverAlt')}>
                {(x) => (
                  <Input
                    {...x}
                    value={f.coverAlt}
                    maxLength={300}
                    disabled={!canWrite || !f.coverFileId}
                    onChange={(e) => set('coverAlt', e.target.value)}
                  />
                )}
              </Field>
              {canWrite && f.coverFileId && (
                <AltTextButton
                  fileId={f.coverFileId}
                  locale={p.locale}
                  context={p.title}
                  onText={(v) => set('coverAlt', v)}
                />
              )}
            </div>
          </div>

          <div className="mb-2 mt-6 flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">{t('ai.studio.f.images')}</p>
            {canWrite && f.images.length < 20 && (
              <ImageGenButton
                label={t('ai.studio.aiImage')}
                sizes
                defaultPrompt=""
                filled={false}
                dirty={dirty}
                loading={imgGen.isPending && imgGen.variables?.step === 'image'}
                onRun={(b) => imgGen.mutate({ step: 'image', ...b })}
              />
            )}
            <HelpTip k="studio.ai_image" />
          </div>
          <ul className="space-y-4">
            {f.images.map((img, i) => (
              <li key={img.fileId} className="rounded-md border border-line p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <ImageUpload
                    value={img.fileId}
                    onChange={(v) =>
                      set(
                        'images',
                        v
                          ? f.images.map((y, j) => (j === i ? { ...y, fileId: v, aiGenerated: false } : y))
                          : f.images.filter((_, j) => j !== i),
                      )
                    }
                    removable={canWrite}
                    label={t('ai.studio.f.imageReplace')}
                  />
                  <Field
                    label={t('ai.studio.f.imageAlt')}
                    className="min-w-[16rem] flex-1"
                    error={err(`images.${i}.alt`)}
                  >
                    {(x) => (
                      <Input
                        {...x}
                        value={img.alt}
                        maxLength={300}
                        disabled={!canWrite}
                        onChange={(e) =>
                          set(
                            'images',
                            f.images.map((y, j) => (j === i ? { ...y, alt: e.target.value } : y)),
                          )
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {p.images.some((y) => y.fileId === img.fileId && y.aiGenerated) && <AiBadge />}
                  {canWrite && (
                    <AltTextButton
                      fileId={img.fileId}
                      locale={p.locale}
                      context={p.title}
                      onText={(v) =>
                        set(
                          'images',
                          f.images.map((y, j) => (j === i ? { ...y, alt: v } : y)),
                        )
                      }
                    />
                  )}
                  {canWrite && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          set(
                            'draft',
                            `${f.draft.trimEnd()}\n\n![${img.alt.replace(/[[\]]/g, '')}](zt-file:${img.fileId})\n`,
                          );
                          toast.success(t('ai.studio.inserted'));
                        }}
                      >
                        {t('ai.studio.insertToDraft')}
                      </Button>
                      <HelpTip k="studio.insert_image" />
                    </>
                  )}
                  <ProvenanceButton fileId={img.fileId} />
                </div>
              </li>
            ))}
          </ul>
          {canWrite && f.images.length < 20 && (
            <div className="mt-3">
              <ImageUpload
                key={f.images.length}
                value={null}
                onChange={(v) =>
                  v && set('images', [...f.images, { fileId: v, alt: '', aiGenerated: false }])
                }
                label={t('ai.studio.f.imageAdd')}
              />
            </div>
          )}
          <p className="mt-3 text-sm text-ink-3">{t('ai.studio.imagesNote')}</p>
        </Section>
      </div>

      {/* 23 Kalite kontrolü */}
      <div id="bolum-quality">
        <Section title={t('ai.studio.sec.quality')} action={<HelpTip k="studio.quality" />}>
          <ul className="space-y-1.5 text-sm">
            {d.quality.map((c) => (
              <li key={c.key} className="flex items-start gap-2">
                <Badge tone={QUALITY_TONE[c.status]}>{t(`ai.studio.qualityStatus.${c.status}`)}</Badge>
                <span>{t(`ai.studio.quality.${c.key}`, c.params ?? {})}</span>
              </li>
            ))}
          </ul>
          {dirty && <p className="mt-2 text-sm text-ink-3">{t('ai.studio.qualityAfterSave')}</p>}
        </Section>
      </div>

      {/* 24–25 Onay ve yayın */}
      <div id="bolum-publish">
        <Section title={t('ai.studio.sec.publish')} action={<HelpTip k="studio.export" />}>
          <p className="mb-3 max-w-[70ch] text-sm text-ink-2">{t('ai.studio.publishLead')}</p>
          <div className="mb-4 flex items-center gap-3">
            <Switch
              checked={f.aiDisclosure}
              onCheckedChange={(v) => set('aiDisclosure', v)}
              label={t('ai.studio.f.aiDisclosure')}
              disabled={!canWrite}
            />
            <span className="text-sm">{t('ai.studio.f.aiDisclosure')}</span>
            <HelpTip k="studio.disclosure" />
          </div>
          {d.post && (
            <p className="mb-3 flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={POST_TONE[d.post.state]}>{t(`admin.blog.states.${d.post.state}`)}</Badge>
              {d.post.reviewStatus !== 'not_required' && (
                <Badge tone={d.post.reviewStatus === 'approved' ? 'moss' : 'amber'}>
                  {t(`admin.pages.reviews.${d.post.reviewStatus}`)}
                </Badge>
              )}
              <Link
                to="/blog/$id"
                params={{ id: d.post.id }}
                className="inline-flex items-center gap-1 text-pine underline"
              >
                {t('ai.studio.openPost')}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </p>
          )}
          {canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                disabled={dirty || !p.draft.trim()}
                loading={exp.isPending}
                onClick={() => exp.mutate(undefined)}
              >
                {p.postId ? t('ai.studio.reexport') : t('ai.studio.export')}
              </Button>
              <ConfirmButton
                variant="ghost"
                message={t('ai.studio.archiveConfirm')}
                onConfirm={() => archive.mutateAsync(undefined)}
              >
                {t('ai.studio.archive')}
              </ConfirmButton>
              <HelpTip k="studio.archive" />
            </div>
          )}
          {dirty && canWrite && <p className="mt-2 text-sm text-ink-3">{t('ai.studio.saveFirst')}</p>}
        </Section>
      </div>

      {/* 26–27 Performans ve güncelleme */}
      <div id="bolum-performance">
        <Section title={t('ai.studio.sec.performance')} action={<HelpTip k="studio.performance" />}>
          {d.performance.status !== 'ok' ? (
            <p className="text-sm text-ink-2">{t(`ai.studio.perf.${d.performance.status}`)}</p>
          ) : (
            <>
              <p className="mb-2 text-sm text-ink-3">
                {t('ai.studio.perfRange', {
                  from: formatDate(`${d.performance.range.from}T12:00:00Z`, false),
                  to: formatDate(`${d.performance.range.to}T12:00:00Z`, false),
                })}
              </p>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[32rem] text-sm">
                  <caption className="sr-only">{t('ai.studio.sec.performance')}</caption>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.studio.perfPeriod')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.studio.clicks')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.studio.impressions')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.studio.ctr')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.studio.position')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {(
                      [
                        ['current', d.performance.current],
                        ['previous', d.performance.previous],
                      ] as const
                    ).map(([k, v]) => (
                      <tr key={k}>
                        <th scope="row" className="px-3 py-2 text-start font-normal">
                          {t(`ai.studio.periods.${k}`)}
                        </th>
                        {v ? (
                          <>
                            <td className="px-3 py-2 text-end tabular-nums">{formatNumber(v.clicks)}</td>
                            <td className="px-3 py-2 text-end tabular-nums">{formatNumber(v.impressions)}</td>
                            <td className="px-3 py-2 text-end tabular-nums">
                              %{formatNumber(v.ctrPermille / 10, 1)}
                            </td>
                            <td className="px-3 py-2 text-end tabular-nums">
                              {v.positionX10 === null ? '—' : formatNumber(v.positionX10 / 10, 1)}
                            </td>
                          </>
                        ) : (
                          <td colSpan={4} className="px-3 py-2 text-ink-3">
                            {t('ai.studio.notComparable')}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {d.performance.queries.length > 0 && (
                <>
                  <h3 className="mt-4 text-sm font-medium">{t('ai.studio.queries')}</h3>
                  <ul className="mt-1 space-y-0.5 text-sm">
                    {d.performance.queries.map((x) => (
                      <li key={x.query}>
                        {x.query}{' '}
                        <span className="text-ink-3">
                          {t('ai.studio.queryStats', {
                            clicks: formatNumber(x.clicks),
                            impressions: formatNumber(x.impressions),
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          <h3 className="mt-6 flex items-center gap-1.5 text-sm font-medium">
            {t('ai.studio.suggestionsTitle')}
            <HelpTip k="studio.suggestions" />
          </h3>
          {!d.suggestions.length ? (
            <p className="text-sm text-ink-3">{t('ai.studio.suggestionsNone')}</p>
          ) : (
            <ul className="mt-1 list-disc space-y-1 ps-5 text-sm">
              {d.suggestions.map((x) => (
                <li key={x.key}>
                  {t(`ai.studio.suggestions.${x.key}`, {
                    ...(x.params ?? {}),
                    ...(x.key === 'low_ctr' && x.params
                      ? { ctr: formatNumber(Number(x.params.ctr) / 10, 1) }
                      : {}),
                    ...(x.key === 'near_first_page' && x.params
                      ? { position: formatNumber(Number(x.params.position) / 10, 1) }
                      : {}),
                  })}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </PageFrame>
  );
}

/** Yapay zekâ adımı: dolu alanın üzerine yazmadan önce onay ister; kaydedilmemiş değişiklik varken çalışmaz. */
function AiStepButton({
  filled,
  dirty,
  loading,
  onRun,
  children,
}: {
  filled: boolean;
  dirty: boolean;
  loading: boolean;
  onRun: (overwrite: boolean) => void;
  children: string;
}) {
  const t = useT();
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      <AiButton
        loading={loading}
        onClick={() => (dirty ? undefined : filled ? setConfirm(true) : onRun(false))}
      >
        {children}
      </AiButton>
      {dirty && <span className="text-sm text-ink-3">{t('ai.studio.saveFirst')}</span>}
      <Dialog
        open={confirm}
        onOpenChange={setConfirm}
        title={children}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirm(false);
                onRun(true);
              }}
            >
              {t('ai.studio.overwrite')}
            </Button>
          </>
        }
      >
        <p className="text-sm">{t('ai.studio.overwriteConfirm')}</p>
      </Dialog>
    </>
  );
}

function AiNote({ r }: { r: AiResult }) {
  const t = useT();
  if (!r.claimFindings.length && !r.maskedCount && !r.truncated) return null;
  return (
    <div role="status" className="mb-3 space-y-1 rounded-md border border-amber/40 bg-amber/5 p-3 text-sm">
      {r.claimFindings.length > 0 && (
        <p>
          {t('ai.studio.claimsFound', { count: r.claimFindings.length })}{' '}
          <span className="text-ink-2">
            {[...new Set(r.claimFindings.map((c) => `"${c.match}"`))].join(', ')}
          </span>
        </p>
      )}
      {r.maskedCount > 0 && <p>{t('ai.studio.masked', { count: r.maskedCount })}</p>}
      {r.truncated && <p>{t('ai.studio.truncated')}</p>}
    </div>
  );
}

function AiBadge() {
  const t = useT();
  return (
    <Badge tone="pine">
      <Sparkles className="me-1 inline h-3.5 w-3.5" aria-hidden />
      {t('ai.studio.aiGenerated')}
    </Badge>
  );
}

/** Yapay zekâ ile görsel: tarif, tarz ve (içerik görselinde) boyut sorulur; ücretli istek. */
function ImageGenButton({
  label,
  sizes,
  defaultPrompt,
  filled,
  dirty,
  loading,
  onRun,
}: {
  label: string;
  sizes: boolean;
  defaultPrompt: string;
  filled: boolean;
  dirty: boolean;
  loading: boolean;
  onRun: (b: { prompt: string; style: string; size: string; overwrite: boolean }) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [style, setStyle] = useState('photo');
  const [size, setSize] = useState('1536x1024');
  const valid = prompt.trim().length >= 10 && prompt.trim().length <= 1000;
  return (
    <>
      <AiButton kind="image" loading={loading} onClick={() => !dirty && setOpen(true)}>
        {label}
      </AiButton>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={label}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!valid}
              onClick={() => {
                setOpen(false);
                onRun({ prompt: prompt.trim(), style, size: sizes ? size : '1536x1024', overwrite: filled });
              }}
            >
              {t('ai.studio.generate')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('ai.studio.imagePrompt')} description={t('ai.studio.imagePromptHint')}>
            {(x) => (
              <Textarea
                {...x}
                rows={3}
                value={prompt}
                maxLength={1000}
                onChange={(e) => setPrompt(e.target.value)}
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('ai.studio.imageStyle')}>
              {(x) => (
                <Select {...x} value={style} onChange={(e) => setStyle(e.target.value)}>
                  {(['photo', 'illustration'] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`ai.studio.imageStyles.${k}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {sizes && (
              <Field label={t('ai.studio.imageSize')}>
                {(x) => (
                  <Select {...x} value={size} onChange={(e) => setSize(e.target.value)}>
                    {(['1536x1024', '1024x1024', '1024x1536'] as const).map((k) => (
                      <option key={k} value={k}>
                        {t(`ai.studio.imageSizes.${k}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
          </div>
          {filled && <p className="text-sm text-amber">{t('ai.studio.overwriteConfirm')}</p>}
          <p className="text-sm text-ink-3">{t('ai.studio.imageCost')}</p>
        </div>
      </Dialog>
    </>
  );
}

/** Yapay zekâ ile alt metin: öneri yalnızca alanı doldurur (kaydetmek kullanıcıda). */
function AltTextButton({
  fileId,
  locale,
  context,
  onText,
}: {
  fileId: string;
  locale: string;
  context: string;
  onText: (v: string) => void;
}) {
  const t = useT();
  const m = useApiMutation(
    () =>
      api.post<{ altText: string }>(
        '/v1/admin/ai/alt-text',
        { fileId, locale, context },
        { idempotencyKey: newIdempotencyKey() },
      ),
    {
      invalidate: [['ai', 'availability']],
      success: t('ai.studio.altDone'),
      onSuccess: (r) => onText(r.altText),
    },
  );
  return (
    <span className="inline-flex items-center gap-1">
      <AiButton loading={m.isPending} onClick={() => m.mutate(undefined)}>
        {t('ai.studio.aiAlt')}
      </AiButton>
      <HelpTip k="studio.ai_alt" />
    </span>
  );
}

interface Provenance {
  source: 'ai_generated' | 'own' | 'licensed' | 'supplier' | 'other';
  license: string | null;
  credit: string | null;
  sourceUrl: string | null;
  aiModel: string | null;
  createdAt: string;
}

/** Görselin kaynağı ve lisansı (U-05). Yapay zekâ ile üretilende kilitli. */
function ProvenanceButton({ fileId }: { fileId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  if (!can('org.file.upload')) return null;
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {t('ai.studio.provenance')}
      </Button>
      <HelpTip k="studio.provenance" />
      {open && <ProvenanceDialog fileId={fileId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ProvenanceDialog({ fileId, onClose }: { fileId: string; onClose: () => void }) {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['file-provenance', fileId],
    queryFn: () => api.get<Provenance | null>(`/v1/admin/files/${fileId}/provenance`),
  });
  const [f, setF] = useState<{ source: string; license: string; credit: string; sourceUrl: string } | null>(
    null,
  );
  useEffect(() => {
    if (q.isSuccess && !f)
      setF({
        source: q.data && q.data.source !== 'ai_generated' ? q.data.source : 'own',
        license: q.data?.license ?? '',
        credit: q.data?.credit ?? '',
        sourceUrl: q.data?.sourceUrl ?? '',
      });
  }, [q.isSuccess, q.data, f]);
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/files/${fileId}/provenance`, {
        source: f!.source,
        license: f!.license || null,
        credit: f!.credit || null,
        sourceUrl: f!.sourceUrl || null,
      }),
    {
      invalidate: [['file-provenance', fileId]],
      success: t('ai.studio.provenanceSaved'),
      onSuccess: onClose,
    },
  );
  const locked = q.data?.source === 'ai_generated';
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('ai.studio.provenanceTitle')}
      closeLabel={t('common.close')}
      footer={
        locked ? (
          <Button onClick={onClose}>{t('common.close')}</Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!f}
              loading={save.isPending}
              onClick={() => save.mutate(undefined)}
            >
              {t('common.save')}
            </Button>
          </>
        )
      }
    >
      {!q.isSuccess || (!locked && !f) ? (
        <Spinner label={t('common.loading')} />
      ) : locked ? (
        <div className="space-y-2 text-sm">
          <AiBadge />
          <p>{t('ai.studio.provenanceAiLocked')}</p>
          <p className="text-ink-3">
            {t('ai.studio.provenanceModel', {
              model: q.data!.aiModel ?? '—',
              date: formatDate(q.data!.createdAt, true),
            })}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={t('ai.studio.provenanceSource')} error={fieldError(save.error, 'source')}>
            {(x) => (
              <Select {...x} value={f!.source} onChange={(e) => setF({ ...f!, source: e.target.value })}>
                {(['own', 'licensed', 'supplier', 'other'] as const).map((k) => (
                  <option key={k} value={k}>
                    {t(`ai.studio.provenanceSources.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('ai.studio.provenanceLicense')}
            description={t('ai.studio.provenanceLicenseHint')}
            error={fieldError(save.error, 'license')}
          >
            {(x) => (
              <Input
                {...x}
                value={f!.license}
                maxLength={300}
                onChange={(e) => setF({ ...f!, license: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('ai.studio.provenanceCredit')} error={fieldError(save.error, 'credit')}>
            {(x) => (
              <Input
                {...x}
                value={f!.credit}
                maxLength={200}
                onChange={(e) => setF({ ...f!, credit: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('ai.studio.provenanceUrl')} error={fieldError(save.error, 'sourceUrl')}>
            {(x) => (
              <Input
                {...x}
                type="url"
                value={f!.sourceUrl}
                onChange={(e) => setF({ ...f!, sourceUrl: e.target.value })}
              />
            )}
          </Field>
          {!q.data && <p className="text-sm text-ink-3">{t('ai.studio.provenanceNone')}</p>}
        </div>
      )}
    </Dialog>
  );
}
